import SafeSummaryTile from "../../../components/common/SafeSummaryTile";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import ImageIcon from "@mui/icons-material/Image";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import PersonIcon from "@mui/icons-material/Person";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { toast } from "react-toastify";

import { axiosPrivate as axiosInstance } from "../../../../../api/axios";
import { EmptyState } from "../../../components/common/EmptyState";
import { SafePageHeader } from "../../../components/common/HelperFunc";
import { useProjectApi } from "../../../hooks/useProjectApi";
import { resolveEmployeeId } from "../../../utils/employeeIdentity";
import { EmployeeSelect } from "../../../components/common/EmployeeSelect";

const PAGE_BG = "#f5f7fb";
const CARD_BORDER = "#e5e7eb";

const getNowInput = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const getCompletedDisplay = (action = {}) => {
  const value = action.completed_at || action.completed_at_input;
  if (!value) return "-";
  return String(value).replace("T", " ").slice(0, 16);
};

const resolvePhotoUrl = (photoUrl) => {
  if (!photoUrl) return "";
  if (/^https?:\/\//i.test(photoUrl)) return photoUrl;
  return `${axiosInstance.defaults.baseURL || ""}${photoUrl}`;
};

const getStatusMeta = (status) => {
  if (status === "done") {
    return {
      label: "완료",
      color: "success",
      icon: <CheckCircleIcon fontSize="small" />,
      bg: "#ecfdf5",
      fg: "#047857",
    };
  }
  if (status === "overdue") {
    return {
      label: "기한초과",
      color: "error",
      icon: <ErrorOutlineIcon fontSize="small" />,
      bg: "#fef2f2",
      fg: "#b91c1c",
    };
  }
  return {
    label: "진행중",
    color: "warning",
    icon: <PendingActionsIcon fontSize="small" />,
    bg: "#fff7ed",
    fg: "#d97706",
  };
};

const getActionSourceMeta = (action = {}) => {
  if (action.inspection_id || action.inspection_content || action.inspected_day) {
    return {
      label: "안전점검 유입",
      description: "안전점검 기록에서 조치요청으로 생성된 항목입니다.",
      color: "#2563eb",
      bg: "#eff6ff",
      border: "#bfdbfe",
    };
  }

  return {
    label: "직접 등록",
    description: "관리자가 직접 등록한 조치 항목입니다.",
    color: "#475569",
    bg: "#f8fafc",
    border: "#e2e8f0",
  };
};

function SummaryTile(props) { return <SafeSummaryTile {...props} />; }

function ActionCard({ action, onComplete, onAssign, focused = false }) {
  const status = getStatusMeta(action.effective_sla_status || action.sla_status);
  const source = getActionSourceMeta(action);
  const done = status.label === "완료";
  const photoUrl = resolvePhotoUrl(action.photo_url);

  return (
    <Card
      data-action-id={action.id}
      variant="outlined"
      sx={{
        height: "100%",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        borderRadius: 2,
        borderColor: focused ? "#7c3aed" : CARD_BORDER,
        bgcolor: "#fff",
        overflow: "hidden",
        borderLeft: "4px solid",
        borderLeftColor: done ? "#16a34a" : status.fg,
        boxShadow: focused ? "0 0 0 3px rgba(124,58,237,.14)" : "none",
      }}
    >
      <CardContent sx={{ p: { xs: 1.75, sm: 2.25 } }}>
        <Stack spacing={{ xs: 1.35, sm: 1.75 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.25}>
            <Box sx={{ minWidth: 0 }}>
              <Chip
                size="small"
                label={source.label}
                title={source.description}
                sx={{
                  height: 24,
                  mb: 0.75,
                  borderRadius: "7px",
                  bgcolor: source.bg,
                  border: "1px solid",
                  borderColor: source.border,
                  color: source.color,
                  fontWeight: 900,
                  "& .MuiChip-label": { px: 0.9 },
                }}
              />
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 900,
                  color: "#111827",
                  lineHeight: 1.35,
                  overflowWrap: "anywhere",
                  wordBreak: "keep-all",
                }}
              >
                {action.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                점검 {action.inspected_day || "-"} {action.inspected_time || ""}
              </Typography>
            </Box>
            <Chip
              size="small"
              icon={status.icon}
              color={status.color}
              label={action.awaiting_assignment ? "담당자 배정 대기" : status.label}
              sx={{ fontWeight: 800, flexShrink: 0 }}
            />
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" color="text.secondary">
            <ScheduleIcon fontSize="small" />
            <Typography variant="body2" sx={{ fontWeight: 800, color: status.fg }}>
              기한 {action.due_day || "-"} {action.due_time || ""}
            </Typography>
          </Stack>

          {done ? (
            <Stack direction="row" spacing={1} alignItems="center" color="text.secondary">
              <CheckCircleIcon fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: 800, color: "#047857" }}>
                완료 일시 {getCompletedDisplay(action)}
              </Typography>
            </Stack>
          ) : null}

          <Stack direction="row" spacing={1} alignItems="center" color="text.secondary">
            <PersonIcon fontSize="small" />
            <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
              담당 {action.assignee_name || action.assignee_emp_id || "-"}
              {action.assignee_department ? ` / ${action.assignee_department}` : ""}
            </Typography>
          </Stack>

          <Divider />

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
              점검 내용
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mt: 0.5,
                color: "#374151",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {action.inspection_content || "점검 내용 없음"}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
              조치 지시
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mt: 0.5,
                color: "#374151",
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {action.instruction || "조치 지시 내용 없음"}
            </Typography>
          </Box>

          {done ? (
            <Alert severity="success" sx={{ borderRadius: 2 }}>
              {action.action_result || "완료 처리된 조치입니다."}
            </Alert>
          ) : null}
        </Stack>
      </CardContent>
      <CardActions
        sx={{
          px: { xs: 1.5, sm: 2.25 },
          pb: { xs: 1.5, sm: 2 },
          pt: 0,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: { xs: "flex-start", sm: "space-between" },
          gap: 1,
          minWidth: 0,
        }}
      >
        <Stack direction="row" spacing={1} sx={{ justifyContent: { xs: "flex-start", sm: "initial" } }}>
          {photoUrl ? (
            <Tooltip title="현장 사진 열기">
              <IconButton
                size="small"
                component="a"
                href={photoUrl}
                target="_blank"
                rel="noreferrer"
                sx={{ border: "1px solid", borderColor: CARD_BORDER, borderRadius: 2 }}
              >
                <ImageIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>
        {action.awaiting_assignment && onAssign ? <Button variant="contained" startIcon={<PersonIcon />} onClick={() => onAssign(action)}>담당자 배정</Button> : !done && !action.awaiting_assignment ? (
          <Button
            size="small"
            variant="contained"
            startIcon={<CheckCircleIcon />}
            onClick={() => onComplete(action)}
            sx={{ flexShrink: 0, minWidth: 72, width: { xs: "100%", sm: "auto" } }}
          >
            완료
          </Button>
        ) : null}
      </CardActions>
    </Card>
  );
}

function CompleteDialog({ open, action, value, completedAt, isSaving, isMobile, onValueChange, onDateChange, onClose, onSubmit }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, lg: 2 },
          maxHeight: { xs: "100dvh", lg: "calc(100% - 64px)" },
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, pt: { xs: 2.5, sm: 2 }, px: { xs: 2, sm: 3 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              조치 완료
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {action?.title || ""}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: PAGE_BG, px: { xs: 2, sm: 3 }, py: 2 }}>
        <Stack spacing={2}>
          <TextField
            fullWidth
            multiline
            minRows={5}
            label="완료 내용"
            placeholder="예: 누유 부위 밸브 교체 완료, 주변 바닥 세척 및 재발 방지 점검 추가"
            value={value}
            onChange={(event) => onValueChange(event.target.value)}
          />
          <TextField
            fullWidth
            type="datetime-local"
            label="완료 일시"
            value={completedAt}
            onChange={(event) => onDateChange(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, gap: 1 }}>
        <Button color="inherit" onClick={onClose} sx={{ flex: { xs: 1, sm: "initial" } }}>
          취소
        </Button>
        <Button variant="contained" disabled={isSaving} onClick={onSubmit} sx={{ flex: { xs: 1, sm: "initial" }, fontWeight: 900 }}>
          {isSaving ? "처리 중" : "완료 처리"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ActionInboxPage({ mode = "mine" }) {
  const { group_code, project_code } = useParams();
  const [searchParams] = useSearchParams();
  const api = useProjectApi(group_code, project_code);
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const user = useSelector((state) => state.auth.user);
  const queryEmpId = searchParams.get("emp_id") || "";
  const focusActionId = searchParams.get("action_id") || searchParams.get("inspection_id") || "";
  const employeeQuery = useQuery({
    queryKey: ["inspection-options", group_code, project_code, user?.user_no, user?.login_id, user?.user_name],
    queryFn: async () => (await api.get("/inspection/options")).data,
    enabled: Boolean(group_code && project_code),
  });
  const employees = employeeQuery.data?.employees || [];
  const employeeIdentity = Object.prototype.hasOwnProperty.call(employeeQuery.data || {}, "current_employee_id")
    ? { emp_id: employeeQuery.data.current_employee_id } : user;
  const defaultEmployeeId = resolveEmployeeId({ user: employeeIdentity, queryEmpId, employees });
  const employeeContext = JSON.stringify([group_code, project_code, queryEmpId, user?.user_no, user?.login_id, user?.user_name]);
  const [employeeSelection, setEmployeeSelection] = useState(null);
  const selectedEmployeeId = employeeSelection?.context === employeeContext ? employeeSelection.id : defaultEmployeeId;
  const assigneeEmpId = employees.some(employee => String(employee.emp_id) === selectedEmployeeId) ? selectedEmployeeId : "";
  const isAdminMode = mode === "all";
  const scope = isAdminMode ? "all" : "mine";
  const employeeLoading = !isAdminMode && employeeQuery.isPending;
  const employeeError = !isAdminMode && employeeQuery.isError;
  const canLoadActions = isAdminMode || Boolean(assigneeEmpId && !employeeLoading && !employeeError);

  const [status, setStatus] = useState("all");
  const [completeTarget, setCompleteTarget] = useState(null);
  const [completeText, setCompleteText] = useState("");
  const [completedAt, setCompletedAt] = useState(getNowInput());
  const [assignment, setAssignment] = useState(null);
  const assignmentPending = useRef(false);
  const [assignmentSaving, setAssignmentSaving] = useState(false);

  const actionsQuery = useQuery({
    queryKey: [
      "inspection-action-inbox",
      group_code,
      project_code,
      scope,
      isAdminMode ? "all" : assigneeEmpId || "unresolved",
    ],
    queryFn: async () => {
      if (!canLoadActions) return [];
      const params = { scope, status: "all" };
      if (!isAdminMode) {
        params.assignee_emp_id = assigneeEmpId;
      }
      const response = await api.get("/inspection/actions", {
        params,
      });
      return response.data || [];
    },
    enabled: Boolean(group_code && project_code && canLoadActions),
    placeholderData: [],
  });

  const inboxLoading = employeeLoading || (canLoadActions && (actionsQuery.isPending || actionsQuery.isPlaceholderData));
  const allActions = useMemo(() => canLoadActions && !actionsQuery.isError ? actionsQuery.data || [] : [], [canLoadActions, actionsQuery.data, actionsQuery.isError]);
  const actions = useMemo(() => {
    if (status === "all") return allActions;
    return allActions.filter((item) => {
      const itemStatus = item.effective_sla_status || item.sla_status;
      if (status === "open") return itemStatus !== "done" && itemStatus !== "overdue";
      return itemStatus === status;
    });
  }, [allActions, status]);
  const summary = useMemo(() => {
    const done = allActions.filter((item) => (item.effective_sla_status || item.sla_status) === "done").length;
    const overdue = allActions.filter((item) => (item.effective_sla_status || item.sla_status) === "overdue").length;
    const open = allActions.filter((item) => {
      const itemStatus = item.effective_sla_status || item.sla_status;
      return itemStatus !== "done" && itemStatus !== "overdue";
    }).length;
    return { total: allActions.length, open, overdue, done };
  }, [allActions]);

  useEffect(() => {
    if (!focusActionId || !canLoadActions || actionsQuery.isFetching || actionsQuery.isError) return undefined;
    const target = allActions.find(
      (item) => String(item.id) === String(focusActionId) || String(item.inspection_id) === String(focusActionId)
    );
    if (!target) {
      toast.info("선택한 후속 조치 항목을 찾을 수 없습니다.");
      return undefined;
    }
    const frame = window.requestAnimationFrame(() => {
      document
        .querySelector(`[data-action-id="${target.id}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [canLoadActions, actionsQuery.isFetching, actionsQuery.isError, allActions, focusActionId]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["inspection-action-inbox", group_code, project_code] });
    queryClient.invalidateQueries({ queryKey: ["risk-inspection-logs", group_code, project_code] });
  };

  const completeMutation = useMutation({
    mutationFn: async () =>
      api.put(`/inspection/actions/${completeTarget.id}/complete`, {
        action_result: completeText,
        completed_at: completedAt,
      }),
    onSuccess: () => {
      toast.success("조치가 완료 처리되었습니다.");
      setCompleteTarget(null);
      setCompleteText("");
      setCompletedAt(getNowInput());
      invalidate();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "조치 완료 처리 중 오류가 발생했습니다.");
    },
  });

  const assign = async () => {
    if (!assignment || assignmentPending.current) return;
    if (!assignment.action_title.trim() || !assignment.assignee_emp_id || !assignment.due_at) {
      toast.warning("조치 제목, 담당자, 기한을 입력해주세요.");
      return;
    }
    assignmentPending.current = true;
    setAssignmentSaving(true);
    try {
      await api.put(`/inspection/logs/${assignment.inspection_id}/assign`, assignment);
      setAssignment(null);
      invalidate();
      toast.success("담당자가 배정되었습니다.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "담당자를 배정하지 못했습니다.");
    } finally {
      assignmentPending.current = false;
      setAssignmentSaving(false);
    }
  };

  const openCompleteDialog = (action) => {
    setCompleteTarget(action);
    setCompleteText(action.action_result || "");
    setCompletedAt(action.completed_at_input || getNowInput());
  };

  const submitComplete = () => {
    if (!String(completeText || "").trim()) {
      toast.warning("완료 내용을 입력해주세요.");
      return;
    }
    completeMutation.mutate();
  };

  return (
    <Box className="safe-page-root"
      sx={{
        minHeight: "100%",
        bgcolor: PAGE_BG,
        p: { xs: 1.25, md: 3 },
        pb: { xs: "max(16px, env(safe-area-inset-bottom))", md: 3 },
        overflowX: "hidden",
        width: "100%",
        maxWidth: "100vw",
        boxSizing: "border-box",
      }}
    >
      <Stack className="safe-page-stack"
        spacing={{ xs: 1.75, md: 2.25 }}
        sx={{
          width: "100%",
          maxWidth: { xs: "calc(100vw - 20px)", md: 1280 },
          minWidth: 0,
          mx: "auto",
        }}
      >
        <SafePageHeader title={isAdminMode ? "점검 조치요청·처리" : "내 조치 업무"}></SafePageHeader>

        {!isAdminMode && <EmployeeSelect key={employeeContext} label="담당자 선택" employees={employees}
          value={assigneeEmpId} teamFilter={false} disabled={employeeLoading || employeeError || completeMutation.isPending}
          sx={{ width: { xs: "100%", sm: 360 }, maxWidth: "100%" }}
          onChange={id => {
            setEmployeeSelection({ context: employeeContext, id });
            setStatus("all");
            setCompleteTarget(null);
          }} />}

        {employeeError ? (
          <Alert severity="error" action={<Button onClick={() => employeeQuery.refetch()}>다시 조회</Button>}>
            담당자 목록을 불러오지 못했습니다.
          </Alert>
        ) : null}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" },
            gap: { xs: 1, md: 1.5 },
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <SummaryTile
              icon={<AssignmentTurnedInIcon />}
              label="전체"
              value={`${summary.total}건`}
              active={status === "all"}
              onClick={() => setStatus("all")}
            />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <SummaryTile
              icon={<PendingActionsIcon />}
              label="진행중"
              value={`${summary.open}건`}
              color="#d97706"
              active={status === "open"}
              onClick={() => setStatus("open")}
            />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <SummaryTile
              icon={<ErrorOutlineIcon />}
              label="기한초과"
              value={`${summary.overdue}건`}
              color="#dc2626"
              active={status === "overdue"}
              onClick={() => setStatus("overdue")}
            />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <SummaryTile
              icon={<CheckCircleIcon />}
              label="완료"
              value={`${summary.done}건`}
              color="#16a34a"
              active={status === "done"}
              onClick={() => setStatus("done")}
            />
          </Box>
        </Box>

        {canLoadActions && actionsQuery.isError ? (
          <Alert severity="error" action={<Button onClick={() => actionsQuery.refetch()}>다시 조회</Button>}>조치함을 불러오는 중 오류가 발생했습니다.</Alert>
        ) : null}

        {inboxLoading ? (
          <Stack alignItems="center" sx={{ py: 8 }}>
            <CircularProgress />
          </Stack>
        ) : null}

        {!inboxLoading && !employeeError && !(canLoadActions && actionsQuery.isError) && !actions.length ? (
          <EmptyState
            compact
            icon={<FactCheckIcon />}
            title={!isAdminMode && !assigneeEmpId ? (employees.length ? "담당자를 선택해주세요" : "등록된 직원이 없습니다") : "표시할 조치가 없습니다"}
            description={
              !isAdminMode && !assigneeEmpId
                ? ""
                : isAdminMode
                ? "안전점검에서 조치요청이 등록되면 이곳에 표시됩니다."
                : "배정된 조치가 생기면 이곳에 표시됩니다."
            }
          />
        ) : null}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))", xl: "repeat(3, minmax(0, 1fr))" },
            gap: { xs: "12px", md: "16px" },
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
          }}
        >
          {actions.map((action) => (
            <Box key={action.id} sx={{ minWidth: 0 }}>
              <ActionCard
                action={action}
                onComplete={openCompleteDialog}
                onAssign={isAdminMode ? action => setAssignment({ inspection_id: action.inspection_id, action_title: action.title, assignee_emp_id: "", due_at: "", instruction: "" }) : undefined}
                focused={
                  Boolean(focusActionId) &&
                  (String(action.id) === String(focusActionId) ||
                    String(action.inspection_id) === String(focusActionId))
                }
              />
            </Box>
          ))}
        </Box>
      </Stack>

      <Dialog open={Boolean(assignment)} onClose={() => !assignmentPending.current && setAssignment(null)} fullWidth maxWidth="sm">
        <DialogTitle>조치 담당자 배정</DialogTitle>
        <DialogContent dividers>
          {assignment && <Stack spacing={2}>
            <TextField label="조치 제목" required value={assignment.action_title} disabled={assignmentSaving} inputProps={{ maxLength: 200 }} onChange={event => setAssignment({ ...assignment, action_title: event.target.value })} />
            <EmployeeSelect label="담당자" required employees={employeeQuery.data?.employees || []} value={assignment.assignee_emp_id} disabled={assignmentSaving} onChange={value => setAssignment({ ...assignment, assignee_emp_id: value })} />
            <TextField label="조치 기한" required type="datetime-local" value={assignment.due_at} disabled={assignmentSaving} InputLabelProps={{ shrink: true }} onChange={event => setAssignment({ ...assignment, due_at: event.target.value })} />
            <TextField label="조치 내용" multiline minRows={3} value={assignment.instruction} disabled={assignmentSaving} onChange={event => setAssignment({ ...assignment, instruction: event.target.value })} />
          </Stack>}
        </DialogContent>
        <DialogActions><Button disabled={assignmentSaving} onClick={() => setAssignment(null)}>취소</Button><Button variant="contained" startIcon={<PersonIcon />} disabled={assignmentSaving || !assignment?.action_title.trim() || !assignment?.assignee_emp_id || !assignment?.due_at} onClick={assign}>배정 저장</Button></DialogActions>
      </Dialog>
      <CompleteDialog
        open={Boolean(completeTarget)}
        action={completeTarget}
        value={completeText}
        completedAt={completedAt}
        isSaving={completeMutation.isPending}
        isMobile={isMobile}
        onValueChange={setCompleteText}
        onDateChange={setCompletedAt}
        onClose={() => setCompleteTarget(null)}
        onSubmit={submitComplete}
      />
    </Box>
  );
}

export function WorkerActionInbox() {
  return <ActionInboxPage mode="mine" />;
}

export function AdminActionInbox() {
  return <ActionInboxPage mode="all" />;
}
